import { useEffect, useState } from 'react';
import { adminApi } from '@formosa/shared/api/admin';
import RichTextEditor from '../../components/editor/RichTextEditor';
import {
  BROADCAST_SEND_TYPE,
  BROADCAST_SEND_TYPE_OPTIONS,
  formatIdList,
  mergeInboxDetailsWithLanguages,
  parseIdList,
  stripInboxDetails,
} from './siteMessageConstants';

const EMPTY_FORM = {
  sendType: BROADCAST_SEND_TYPE.ALL,
  idsText: '',
  imgUrl: '',
  filmUrl: '',
  details: [],
};

export default function SiteMessageBroadcastEditModal({ open, broadcastId, onClose, onSaved }) {
  const [form, setForm] = useState(EMPTY_FORM);
  const [languages, setLanguages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const isEdit = Boolean(broadcastId);

  useEffect(() => {
    if (!open) {
      return;
    }
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError('');
      try {
        const langList = await adminApi.newsLanguages();
        if (cancelled) {
          return;
        }
        setLanguages(langList || []);
        if (broadcastId) {
          const data = await adminApi.getBroadcastInbox(broadcastId);
          if (cancelled) {
            return;
          }
          setForm({
            sendType: data.sendType ?? BROADCAST_SEND_TYPE.ALL,
            idsText: formatIdList(data.memberIds),
            imgUrl: data.imgUrl || '',
            filmUrl: data.filmUrl || '',
            details: mergeInboxDetailsWithLanguages(langList, data.details),
          });
        } else {
          setForm({
            ...EMPTY_FORM,
            details: mergeInboxDetailsWithLanguages(langList, []),
          });
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.message || '載入失敗');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [open, broadcastId]);

  function updateDetail(index, key, value) {
    setForm((prev) => {
      const nextDetails = [...prev.details];
      nextDetails[index] = { ...nextDetails[index], [key]: value };
      return { ...prev, details: nextDetails };
    });
  }

  async function handleSubmit(event) {
    event.preventDefault();
    setError('');
    const details = stripInboxDetails(form.details).filter((d) => d.title?.trim());
    if (!details.length) {
      setError('至少填寫一個語系的標題');
      return;
    }
    const sendType = Number(form.sendType);
    const ids = parseIdList(form.idsText);
    if (sendType !== BROADCAST_SEND_TYPE.ALL && !ids.length) {
      setError(sendType === BROADCAST_SEND_TYPE.AGENT ? '請輸入代理ID' : '請輸入會員ID');
      return;
    }
    const payload = {
      sendType,
      ids,
      imgUrl: form.imgUrl?.trim() || '',
      filmUrl: form.filmUrl?.trim() || '',
      details,
    };
    setSubmitting(true);
    try {
      if (isEdit) {
        await adminApi.updateBroadcastInbox({ id: broadcastId, ...payload });
      } else {
        await adminApi.createBroadcastInbox(payload);
      }
      onSaved?.(isEdit);
      onClose?.();
    } catch (err) {
      setError(err.message || '保存失敗');
    } finally {
      setSubmitting(false);
    }
  }

  if (!open) {
    return null;
  }

  const idsLabel = Number(form.sendType) === BROADCAST_SEND_TYPE.AGENT ? '代理ID' : '用戶ID';

  return (
    <div className="confirm-dialog-overlay" role="presentation" onClick={onClose}>
      <div
        className="modal-panel modal-panel--wide"
        role="dialog"
        aria-modal="true"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-panel__header">
          <h3>{isEdit ? '修改群發站內信' : '新增群發站內信'}</h3>
          <button type="button" className="btn btn-link" onClick={onClose}>
            ×
          </button>
        </div>

        {loading ? (
          <p className="table-empty">載入中…</p>
        ) : (
          <form className="modal-panel__body site-message-edit-form" onSubmit={handleSubmit}>
            {error ? <p className="form-message form-message--error">{error}</p> : null}

            <div className="form-field">
              <span className="form-field__label">群發類型</span>
              <div className="product-status-radios">
                {BROADCAST_SEND_TYPE_OPTIONS.map((option) => (
                  <label key={option.value} className="product-status-radios__item">
                    <input
                      type="radio"
                      name="broadcastSendType"
                      checked={Number(form.sendType) === option.value}
                      onChange={() => setForm((p) => ({ ...p, sendType: option.value, idsText: '' }))}
                      disabled={isEdit}
                    />
                    {option.label}
                  </label>
                ))}
              </div>
            </div>

            {Number(form.sendType) !== BROADCAST_SEND_TYPE.ALL ? (
              <div className="form-field">
                <label htmlFor="broadcastIds">
                  <span className="form-label-required">{idsLabel}</span>
                </label>
                <input
                  id="broadcastIds"
                  type="text"
                  value={form.idsText}
                  onChange={(e) => setForm((p) => ({ ...p, idsText: e.target.value }))}
                  placeholder="多個ID以逗號分隔"
                  disabled={isEdit}
                />
              </div>
            ) : (
              <div className="form-field">
                <span className="form-field__label">用戶ID</span>
                <input type="text" value="所有用戶" disabled />
              </div>
            )}

            <div className="form-field">
              <label htmlFor="broadcastImgUrl">圖片連結</label>
              <input
                id="broadcastImgUrl"
                type="text"
                value={form.imgUrl}
                onChange={(e) => setForm((p) => ({ ...p, imgUrl: e.target.value }))}
              />
            </div>

            <div className="form-field">
              <label htmlFor="broadcastFilmUrl">影片連結</label>
              <input
                id="broadcastFilmUrl"
                type="text"
                value={form.filmUrl}
                onChange={(e) => setForm((p) => ({ ...p, filmUrl: e.target.value }))}
                placeholder="請輸入影片連結（選填）"
              />
            </div>

            {form.details.map((detail, index) => (
              <section key={detail.lang} className="site-message-lang-section">
                <div className="form-field">
                  <label htmlFor={`broadcast-title-${detail.lang}`}>
                    <span className="form-label-required">標題（{detail._label || detail.lang}）</span>
                  </label>
                  <input
                    id={`broadcast-title-${detail.lang}`}
                    type="text"
                    value={detail.title || ''}
                    onChange={(e) => updateDetail(index, 'title', e.target.value)}
                  />
                </div>
                <div className="form-field form-field--full">
                  <span className="form-label">內容（{detail._label || detail.lang}）</span>
                  <RichTextEditor
                    value={detail.body || ''}
                    onChange={(value) => updateDetail(index, 'body', value)}
                  />
                </div>
              </section>
            ))}

            <div className="modal-panel__footer admin-modal__footer--center">
              <button type="submit" className="btn btn-success" disabled={submitting}>
                {submitting ? '提交中…' : '提交'}
              </button>
              <button type="button" className="btn btn-danger" onClick={onClose} disabled={submitting}>
                關閉
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
